<!-- PermissionForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Store
import { usePermissionStore } from "@/stores/usePermissionStore";

// Componentes modais
import AddPermissionForm from "./AddPermissionForm.vue";
import EditPermissionForm from "./EditPermissionForm.vue";
import DeletePermissionForm from "./DeletePermissionForm.vue";
import PermissionCardModal from "./PermissionCardModal.vue";
import BatchDeletePermissionForm from "./BatchDeletePermissionForm.vue";
import SearchPermissionInput from "./SearchPermissionInput.vue";
import PermissionHelpModal from "./PermissionHelpModal.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const permissionStore = usePermissionStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCardModal = ref(false);
const selectedPermissionId = ref(null);

// Modal de ajuda
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ padrão TagForm.vue
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================

// TODO: Implementar lógica real de permissões se necessário
function canEdit() {
  return true;
}
function canView() {
  return true;
}
function canDelete() {
  return true;
}

// ✅ itens editáveis na página
const anyEditableOnPage = computed(() =>
  permissionStore.permissions.some((p) => canEdit(p))
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = permissionStore.permissions
      .filter((p) => canEdit(p))
      .map((p) => p.permissionId);

    if (editableIdsOnPage.length === 0) return false;

    return editableIdsOnPage.every((id) =>
      permissionStore.selectedPermissionIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = permissionStore.permissions
      .filter((p) => canEdit(p))
      .map((p) => p.permissionId);

    const allIdsOnPage = permissionStore.permissions.map((p) => p.permissionId);

    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!permissionStore.selectedPermissionIds.includes(id)) {
          permissionStore.selectedPermissionIds.push(id);
        }
      });
    } else {
      permissionStore.selectedPermissionIds =
        permissionStore.selectedPermissionIds.filter(
          (id) => !allIdsOnPage.includes(id)
        );
    }
  },
});

// ✅ habilita / desabilita ações em lote
const canBatchActions = computed(() => {
  if (
    !permissionStore.selectedPermissionIds ||
    permissionStore.selectedPermissionIds.length === 0
  ) {
    return false;
  }

  const selectedOnPage = permissionStore.permissions.filter((p) =>
    permissionStore.selectedPermissionIds.includes(p.permissionId)
  );

  if (selectedOnPage.length === 0) return false;

  return selectedOnPage.every((p) => canEdit(p));
});

function toggleRowDropdown(permissionId) {
  openDropdownId.value =
    openDropdownId.value === permissionId ? null : permissionId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const permission = permissionStore.permissions[index];
  selectedPermissionId.value = permission.permissionId;
  permissionStore.setSelectedPermission(permission);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  permissionStore.togglePermissionSelection(id);
}

// Fetch de dados
async function fetchPermissions() {
  try {
    isLoadingTable.value = true;
    await permissionStore.fetchPermissionsPaged(
      currentPage.value,
      pageSize.value,
      permissionStore.permissionSearchTerm
    );
  } catch (error) {
    console.error("❌ Erro ao buscar permissões paginadas:", error?.message);
    showErrorToast("Erro ao carregar permissões.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchPermissions);

// ====================================================================
// ✅ Handler único pós-mudança
// ====================================================================
async function handlePermissionsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) {
    permissionStore.selectedPermissionIds = [];
  }

  if (successMessage) {
    showSuccessToast(successMessage);
  }

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchPermissions();
    return;
  }

  await fetchPermissions();
}
// ====================================================================

// ✅ Exclusão em lote validando seleção NA PÁGINA
function tryOpenBatchDeleteModal() {
  const idsOnPage = permissionStore.permissions.map((p) => p.permissionId);
  const selectedOnPage = permissionStore.selectedPermissionIds.filter((id) =>
    idsOnPage.includes(id)
  );

  isBatchDropdownOpen.value = false;

  if (selectedOnPage.length === 0) {
    showErrorToast("Nenhuma permissão selecionada.");
    return;
  }

  showBatchDeleteModal.value = true;
}

// Lógica para fechar dropdowns
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchPermissions();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  permissionStore.permissionSearchTerm = "";
  permissionStore.currentPage = 1;
  permissionStore.shouldForcePageOne = true;

  permissionStore.selectedPermissionIds = [];
  permissionStore.selectedPermission = null;
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedPermissionId.value = null;

  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  showHelpModal.value = false;

  isLoadingTable.value = false;
});

// Seleciona a permissão
function indexSelector(index) {
  const permission = permissionStore.permissions[index];
  permissionStore.setSelectedPermission(permission);
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Permissões</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie as permissões do sistema, que serão utilizadas para
            controlar o acesso a textos, atividades, notas e outros recursos.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchPermissionInput />
        </div>

        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Permissão
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 60px">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 30%">Nome</th>
              <th style="width: 25%">Descrição</th>
              <th style="width: 12%">Tabela referenciada</th>
              <th style="width: 12%">Criação</th>
              <th style="width: 12%">Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(permission, index) in !isLoadingTable ? permissionStore.permissions : []"
              :key="permission.permissionId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td @click.stop>
                <input
                  :class="{ 'disabled-item': !canEdit(permission) }"
                  type="checkbox"
                  :value="permission.permissionId"
                  :checked="
                    permissionStore.selectedPermissionIds?.includes(
                      permission.permissionId
                    )
                  "
                  @change="toggleSelection(permission.permissionId)"
                  class="checkbox-style"
                  :disabled="!canEdit(permission)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(permission.permissionId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === permission.permissionId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canEdit(permission) }"
                      @click="canEdit(permission) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li
                      :class="{ 'disabled-item': !canView(permission) }"
                      @click="canView(permission) && handleView(index)"
                    >
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete(permission) }"
                      @click="canDelete(permission) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="permission.permissionId">{{
                  permission.permissionId
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="permission.permissionName">
                  {{ toTitleCase(permission.permissionName) }}
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="permission.description">{{
                  permission.description
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="permission.referencedTable">{{
                  permission.referencedTable
                }}</span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(permission.createdAt)">
                  {{ formatDateTimeForDisplay(permission.createdAt) }}
                </span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(permission.updatedAt)">
                  {{ formatDateTimeForDisplay(permission.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando permissões...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(permissionStore.totalPermissionsCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhuma permissão encontrada com os filtros aplicados.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (permissionStore.totalPermissionsCount ?? 0) > 0"
          class="permission-pagination-row"
        >
          <div class="permission-pagination-left">
            <span class="permission-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="permissionStore.totalPermissionsCount ?? 0"
            />
            <span>permissões</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="permissionStore.totalPermissionsCount"
            :total-pages="permissionStore.totalPermissionsPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddPermissionForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="
        handlePermissionsChanged({ goToFirstPage: true, closeAdd: true })
      "
    />

    <EditPermissionForm
      v-if="showEditModal"
      :permissionId="permissionStore.selectedPermission?.permissionId"
      @close="showEditModal = false"
      @refresh="
        handlePermissionsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <PermissionCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :permission-id="selectedPermissionId"
      @close="showCardModal = false"
    />

    <DeletePermissionForm
      v-if="showDeleteModal"
      :permission="permissionStore.selectedPermission"
      @close="showDeleteModal = false"
      @refresh="
        handlePermissionsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeletePermissionForm
      v-if="showBatchDeleteModal"
      :permissionIds="permissionStore.selectedPermissionIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handlePermissionsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <PermissionHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}

.permission-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}

.permission-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.permission-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>